import { COMMUNICATION_ROUTES } from '@/modules/communication/routes';
import { CUSTOMERS_ROUTES } from '@/modules/customers/routes';
import { FOLLOW_UP_ROUTES } from '@/modules/follow-up/routes';
import { POSITION_ROUTES } from '@/modules/positions/routes';
import { AI_MATCHING_ROUTES } from '@/modules/ai-matching/routes';
import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { CALL_LISTS_ROUTES } from '../modules/call-lists/routes';
import { CANDIDATE_ROUTES } from '../modules/candidates/routes';
import { MATCHING_ROUTES } from '../modules/matching/routes';

import { NXT_AUTH_GUARD } from '@nxtpeople/nxt-vue-keycloak';
import { QUERY_GUARD } from '@nxtpeople/nxt-vue-library';
import AUTH_GUARD from './guards/auth.guard';
import PERMISSION_GUARD from './guards/permission.guard';
import SLIDE_TRANSITION_GUARD from './guards/slide-transition.guard';
import TITLE_GUARD from './guards/title.guard';
import UNAVAILABLE_GUARD from './guards/unavailable.guard';

const main = () => import('./pages/Main.vue');

const candidates = () => import('../modules/candidates/CandidatesIndex.vue');
const positions = () => import('../modules/positions/PositionsIndex.vue');
const customers = () => import('../modules/customers/CustomersIndex.vue');
const matching = () => import('../modules/matching/MatchingIndex.vue');
const callLists = () => import('../modules/call-lists/CallListsIndex.vue');
const followUp = () => import('../modules/follow-up/FollowUpIndex.vue');
const communications = () => import('../modules/communication/CommunicationsIndex.vue');
const aiMatching = () => import('../modules/ai-matching/AiMatchingIndex.vue');
const reporting = () => import('../modules/reporting/index.vue');

const forbiddenPage = () => import('./pages/ForbiddenPage.vue');
const error = () => import('./pages/Error.vue');
const bugReport = () => import('./pages/BugReport.vue');
const unavailable = () => import('./pages/Unavailable.vue');

export const privateRoutes: RouteRecordRaw[] = [
   {
      path: '/candidates',
      name: 'Candidates',
      component: candidates,
      children: CANDIDATE_ROUTES,
      meta: {
         titleKey: 'candidates.candidate',
      },
   },
   {
      path: '/candidate/:sync_id',
      name: 'CandidateSearch',
      redirect: (to) => {
         return { path: '/candidates', query: { sync_id: to.params.sync_id } };
      },
   },
   {
      path: '/positions',
      name: 'Positions',
      component: positions,
      children: POSITION_ROUTES,
      meta: {
         titleKey: 'positions.position',
      },
   },
   {
      path: '/customers',
      name: 'Customers',
      component: customers,
      children: CUSTOMERS_ROUTES,
      meta: {
         titleKey: 'customers.customer',
      },
   },
   {
      path: '/customer/:sync_id',
      name: 'CustomerSearch',
      redirect: (to) => {
         return { path: '/customers', query: { sync_id: to.params.sync_id } };
      },
   },
   {
      path: '/matching',
      name: 'Matching',
      component: matching,
      children: MATCHING_ROUTES,
      meta: {
         titleKey: 'matching.match',
      },
   },
   {
      path: '/call-lists',
      name: 'CallLists',
      component: callLists,
      children: CALL_LISTS_ROUTES,
      meta: {
         titleKey: 'call-lists.call-list',
      },
   },
   {
      path: '/follow-up',
      name: 'FollowUp',
      component: followUp,
      children: FOLLOW_UP_ROUTES,
      meta: {
         titleKey: 'follow-up.follow-up',
      },
   },
   {
      path: '/communications',
      name: 'Communications',
      component: communications,
      children: COMMUNICATION_ROUTES,
      meta: {
         titleKey: 'communications.communication',
      },
   },
   {
      path: '/ai-matching',
      name: 'AiMatching',
      component: aiMatching,
      children: AI_MATCHING_ROUTES,
      meta: {
         titleKey: 'ai-matching.ai-matching',
      },
   },
   {
      path: '/reporting',
      name: 'Reporting',
      component: reporting,
      meta: { titleKey: 'reporting.reporting' },
   },
   {
      path: '/bug-report',
      name: 'BugReport',
      component: bugReport,
   },
   {
      path: '/forbidden',
      name: 'Forbidden',
      component: forbiddenPage,
      meta: { error: true },
   },
];

export const publicRoutes: Array<RouteRecordRaw> = [
   {
      path: '/unavailable',
      name: 'Unavailable',
      component: unavailable,
   },
   {
      path: '/error',
      name: 'Error',
      component: error,
      meta: { error: true },
   },
];

export const routes: Array<RouteRecordRaw> = [
   {
      path: '/',
      alias: '',
      component: main,
      children: [{ path: '', redirect: '/candidates' }, ...privateRoutes],
   },
   ...publicRoutes,
   {
      path: '/:pathMatch(.*)*',
      redirect: '/candidates',
   },
];

const router = createRouter({
   history: createWebHistory(import.meta.env.BASE_URL),
   routes,
});

// unavailable guard
router.beforeEach(UNAVAILABLE_GUARD);

// Authentication guard
router.beforeEach((to, from, next) => NXT_AUTH_GUARD(to, from, next, { publicRoutes }));

// Authorization guards (user info & permissions)
router.beforeEach(AUTH_GUARD);
router.beforeEach(PERMISSION_GUARD);

// other guards
router.beforeEach(SLIDE_TRANSITION_GUARD);
router.beforeEach(TITLE_GUARD);
router.beforeEach(QUERY_GUARD);

export default router;
